import { Injectable, Signal, WritableSignal, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { IDisplaySlide } from '@pcp/core/interfaces';
import { extractSlideTypeCategory } from '@pcp/core/util';
import { ClassroomFacade } from '../yjs/classroom.facade';
import * as fromClassroom from '../store/lesson.selectors';
import { BookpageSignals, LayerSignals } from './layer-signals';
import { shouldKeepViewport } from './util/active-element-keep-viewport';

type Layer = 1 | 2 | 3;

const DEFAULT_VIEWBOX = '0 0 1280 720';
const DEFAULT_TEXTBOARD_PAGE = 1;

/**
 * Client-local display state derived from the classroom Y.Doc (effective viewBox / textboard page,
 * layer visibility, slide-identity facets). The beamer navigation preview's state is NOT here - it
 * lives in `NavigationPreviewStore` (`shared/classroom/preview-screen/feature-shell`).
 */
@Injectable({ providedIn: 'root' })
export class ActiveElementUiStateService {
    private readonly store = inject(Store);
    private readonly lessonYjs = inject(ClassroomFacade);
    private readonly isLessonPaused: Signal<boolean> = toSignal(
        this.store.select(fromClassroom.isLessonPaused),
        { initialValue: false }
    );

    /**
     * Effective viewBox per layer — read by the keep-viewport decision in
     * `ClassroomFacade.showSlideOnLayer`. Updated by:
     *   1. The slide-change tracker (resets to default; preserves prev viewBox when
     *      `shouldKeepViewport` matches the hardcoded slide-pair table).
     *   2. The Y.Doc viewBox echo (remote pan, or own pan once the settle write lands).
     *
     * There is no local-write path any more: the SVG era's `setLocalViewBox` (synchronous
     * local pan feedback, "anticipate the Y.Doc echo") lost its caller when the SVG renderer
     * was deleted (2026-07-15) and was removed. Local-first pan feedback now lives in
     * `FabricSlideCanvas.liveViewBox`, which overrides the synced value for display only and
     * never touches this service (`panorama-viewport-sync-perf`).
     *
     * On a fresh service instance the value is the schema default `'0 0 1280 720'`. The
     * Y.Doc echo effect overwrites it as soon as `state` Signal hydrates.
     */
    private readonly primaryEffectiveViewBoxSig: WritableSignal<string> = signal(DEFAULT_VIEWBOX);
    private readonly secondaryEffectiveViewBoxSig: WritableSignal<string> = signal(DEFAULT_VIEWBOX);
    private readonly tertiaryEffectiveViewBoxSig: WritableSignal<string> = signal(DEFAULT_VIEWBOX);

    /**
     * Effective textboard page per layer. Same shape as effective viewBox: slide-change
     * resets to `1`; Y.Doc-echo overwrites; local user-click via
     * `setLocalTextboardPage(layer, page)` updates immediately (the Y.Doc write is
     * debounced 100ms by callers; without the local override the display would lag).
     *
     * Only meaningful for textboard-type slides; default value for other slide types
     * is harmless.
     */
    private readonly primaryEffectiveTextboardPageSig: WritableSignal<number> = signal(DEFAULT_TEXTBOARD_PAGE);
    private readonly secondaryEffectiveTextboardPageSig: WritableSignal<number> = signal(DEFAULT_TEXTBOARD_PAGE);
    private readonly tertiaryEffectiveTextboardPageSig: WritableSignal<number> = signal(DEFAULT_TEXTBOARD_PAGE);

    /**
     * Layer is "hidden" iff the lesson is paused OR a higher layer currently has a slide.
     * Read via `isHiddenForLayer(layer)`.
     */
    private readonly primaryIsHidden: Signal<boolean> = computed(
        () => this.isLessonPaused() || this.lessonYjs.secondarySlide() != null || this.lessonYjs.tertiarySlide() != null
    );
    private readonly secondaryIsHidden: Signal<boolean> = computed(
        () => this.isLessonPaused() || this.lessonYjs.tertiarySlide() != null
    );
    private readonly tertiaryIsHidden: Signal<boolean> = computed(() => this.isLessonPaused());

    /**
     * Slide-identity Signal facets per layer (replaces `from{Primary,Secondary,Tertiary}Element.*`
     * NgRx selectors). Constructed once over the facade's per-layer slide + hotspot Signals.
     */
    readonly primaryLayerSignals = new LayerSignals(
        this.lessonYjs.primarySlide,
        this.lessonYjs.primarySlideIsAccessedViaHotspot
    );
    readonly secondaryLayerSignals = new LayerSignals(
        this.lessonYjs.secondarySlide,
        this.lessonYjs.secondarySlideIsAccessedViaHotspot
    );
    readonly tertiaryLayerSignals = new LayerSignals(
        this.lessonYjs.tertiarySlide,
        this.lessonYjs.tertiarySlideIsAccessedViaHotspot
    );

    /**
     * Bookpage Signal facets — derived from primary + secondary slide. Replaces
     * `fromBookpages.*` NgRx selectors.
     */
    readonly bookpageSignals = new BookpageSignals(
        this.lessonYjs.primarySlide,
        this.lessonYjs.secondarySlide
    );

    /**
     * The fallback image is shown when the primary layer has no real slide (`'none'`
     * type category — empty sentinel) OR when the lesson is paused. Replaces
     * `selectShouldShowFallbackImage` from `active-element.selectors.ts`.
     */
    readonly shouldShowFallbackImage: Signal<boolean> = computed(() => {
        const primaryCategory = extractSlideTypeCategory(
            this.lessonYjs.primarySlide() as unknown as IDisplaySlide | null
        );
        return primaryCategory === 'none' || this.isLessonPaused();
    });

    /**
     * Difficult-question numbers of the topmost-visible slide. Replaces
     * `selectDifficultQuestionNumbers` from `navigation-preview.selectors.ts` (which
     * also used `from{Primary,Secondary,Tertiary}Element.selectSlide`).
     */
    readonly difficultQuestionNumbers = computed(() => {
        const slide = this.lessonYjs.currentLayerSlide() as unknown as IDisplaySlide | null;
        return slide?.difficultQuestionNumbers;
    });

    constructor() {
        this.wireViewBoxForLayer(1, this.primaryEffectiveViewBoxSig);
        this.wireViewBoxForLayer(2, this.secondaryEffectiveViewBoxSig);
        this.wireViewBoxForLayer(3, this.tertiaryEffectiveViewBoxSig);

        this.wireSlideChangeTextboardPageReset(1, this.primaryEffectiveTextboardPageSig);
        this.wireSlideChangeTextboardPageReset(2, this.secondaryEffectiveTextboardPageSig);
        this.wireSlideChangeTextboardPageReset(3, this.tertiaryEffectiveTextboardPageSig);
        this.wireYDocTextboardPageEcho(1, this.primaryEffectiveTextboardPageSig);
        this.wireYDocTextboardPageEcho(2, this.secondaryEffectiveTextboardPageSig);
        this.wireYDocTextboardPageEcho(3, this.tertiaryEffectiveTextboardPageSig);
    }

    /**
     * Single per-layer effect that owns both responsibilities:
     *   - Slide-id change: keep (for the shouldKeepViewport pair table) or reset to default.
     *   - Steady-state Y.Doc viewBox echo: mirror the Y.Doc value (local pan after-write,
     *     remote pan, late-joiner hydration).
     *
     * Coordinating both in one effect lets the slide-change branch take precedence within
     * the same Y.Map emission. The binding's `setSlide()` resets `${prefix}SlideViewBox`
     * to default in the same transaction that writes the new slide — without this
     * coordination, the Y.Doc echo would clobber the kept value (visible flicker for
     * the keep-viewport slide pairs — module 203 13691↔13692, module 252 444673↔444759).
     *
     * A separate `setSlideViewBox` call (local pan, remote pan) emits its own Y.Map
     * change with the slide unchanged, so this effect runs again with no slide-id
     * change and falls through to the echo branch.
     */
    private wireViewBoxForLayer(layer: Layer, effective: WritableSignal<string>): void {
        let prevSlide: { id?: number; moduleId?: number; type?: string } | null = null;
        const slideSignal = this.lessonYjs.slideForLayer(layer);
        const viewBoxSignal = this.lessonYjs.viewBoxForLayer(layer);

        effect(() => {
            const slide = slideSignal();
            const ydocViewBox = viewBoxSignal();
            const prev = prevSlide;
            const slideIdChanged = (slide?.id ?? null) !== (prev?.id ?? null);
            prevSlide = slide;

            if (slideIdChanged && slide && prev) {
                if (shouldKeepViewport(prev as IDisplaySlide, slide as unknown as IDisplaySlide)) {
                    return;
                }
                effective.set(DEFAULT_VIEWBOX);
                return;
            }

            if (ydocViewBox !== undefined) {
                effective.set(ydocViewBox);
            }
        });
    }

    private wireSlideChangeTextboardPageReset(layer: Layer, effective: WritableSignal<number>): void {
        let prevSlideId: number | undefined = undefined;
        const slideSignal = this.lessonYjs.slideForLayer(layer);

        effect(() => {
            const slide = slideSignal();
            const prev = prevSlideId;
            prevSlideId = slide?.id;
            if (slide && prev !== undefined && prev !== slide.id) {
                effective.set(DEFAULT_TEXTBOARD_PAGE);
            }
        });
    }

    private wireYDocTextboardPageEcho(layer: Layer, effective: WritableSignal<number>): void {
        const pageSignal = this.lessonYjs.textboardPageForLayer(layer);
        effect(() => {
            const ydocPage = pageSignal();
            if (ydocPage !== undefined) {
                effective.set(ydocPage);
            }
        });
    }

    /**
     * Effective viewBox per layer — for SVG-canvas / panorama display. (Scroll-video no longer
     * consumes it: frames render on the fabric canvas via a synced frame index —
     * `canvas-scroll-video-slides`.)
     * Use this Signal in components and effects; do NOT read `lessonYjs.viewBoxForLayer`
     * directly because that bypasses the slide-change reset logic.
     */
    effectiveViewBoxForLayer(layer: Layer): Signal<string> {
        return layer === 1
            ? this.primaryEffectiveViewBoxSig
            : layer === 2
              ? this.secondaryEffectiveViewBoxSig
              : this.tertiaryEffectiveViewBoxSig;
    }

    /**
     * Layer-hidden state per layer. Use this Signal in components — `true` means the
     * layer should be visually hidden (paused, or a higher layer is open).
     */
    isHiddenForLayer(layer: Layer): Signal<boolean> {
        return layer === 1
            ? this.primaryIsHidden
            : layer === 2
              ? this.secondaryIsHidden
              : this.tertiaryIsHidden;
    }

    /**
     * Effective textboard page per layer. Use this Signal in components / effects;
     * do NOT read the Y.Doc textboard-page Signal directly because that bypasses the
     * slide-change reset and the local-set-before-debounced-Y.Doc-write pattern.
     */
    effectiveTextboardPageForLayer(layer: Layer): Signal<number> {
        return layer === 1
            ? this.primaryEffectiveTextboardPageSig
            : layer === 2
              ? this.secondaryEffectiveTextboardPageSig
              : this.tertiaryEffectiveTextboardPageSig;
    }

    /**
     * Set the effective textboard page for `layer` immediately (no Y.Doc round-trip).
     * Call this from `onSvgTextboardPageNumberChanged` BEFORE queueing the debounced Y.Doc
     * write — otherwise the display lags by the debounce window.
     */
    setLocalTextboardPage(layer: Layer, page: number): void {
        const sig = layer === 1
            ? this.primaryEffectiveTextboardPageSig
            : layer === 2
              ? this.secondaryEffectiveTextboardPageSig
              : this.tertiaryEffectiveTextboardPageSig;
        sig.set(page);
    }
}

export type { Layer as ActiveElementLayer };
